import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableBody from '@mui/material/TableBody';
import Checkbox from '@mui/material/Checkbox';
import MuiButton from '@mui/material/Button';
import { toast } from '../../../../ui/toast';
import { AddIcon, RefreshIcon } from '../../../../ui/icons/icons';
import { Modal } from '../../../../ui/modal/modal';
import { PaginationController } from '../../../../ui/pagination_controller/pagination_controller';
import {
  addUserGroup,
  deleteUserGroup,
  editUserGroup,
} from '../../../../utils/api_service/user_groups';
import { Permission } from '../../../../utils/helpers/permissions';
import PermissionGate from '../../../../utils/hooks/PermissionGate';
import {
  addUserGroupRedux,
  deleteUserGroupRedux,
  editUserGroupRedux,
  fetchUserGroups,
} from '../../../../utils/redux_store/features/user_group_reducer';
import { RootState, store } from '../../../../utils/redux_store/store';
import { UserGroup } from '../../../../utils/types/admin_types';
import { SelectOptions } from '../../../../utils/types/react_select_types';
import TableSkeletonLoader from '../../../../ui/TableSkeletonLoader';
import baseStyles from '../../admin.module.css';
import { AdminSearchBar } from '../admin_search_bar/admin_search_bar';
import { GroupRow } from './group_row/group_row';
import { AddGroupModal } from './modals/add_group_modal';
import { EditGroupModal } from './modals/edit_group_modal';
import { useGroupSearch } from './useGroupSearch';

export const GroupManagement = () => {
  const userGroupsState = useSelector((state: RootState) => state.userGroups);
  const userGroups = userGroupsState?.userGroupList || [];
  const isLoading = userGroupsState?.loading === 'pending';
  const userRole = useSelector(
    (state: RootState) => state.userRoles?.userRoleList || [],
  );
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false);
  const [editModalVisible, setEditModalVisible] = useState<boolean>(false);
  const [editedGroup, setEditedGroup] = useState<UserGroup | undefined>(userGroups[0]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [pageNumber, setPageNumber] = useState<number>(1);
  const { paginatedGroups, totalPages } = useGroupSearch(
    userGroups,
    searchTerm,
    pageNumber,
    8,
  );

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchTerm(event.target.value);
    setPageNumber(1);
  };

  const addGroup = async (groupName: string, roles: SelectOptions[]) => {
    const roleList = roles.map((role) => role.value);
    try {
      const response = await addUserGroup(groupName, roleList);
      if (response.ok) {
        const data = await response.json();
        setAddModalVisible(false);
        const updatedGroup = {
          id: data.id,
          organizationId: data.organizationId,
          groupName: groupName,
          roles: userRole.filter((item) => roleList.includes(item.id)),
        };
        store.dispatch(addUserGroupRedux(updatedGroup));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error adding group: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const deleteGroup = async (id: string) => {
    try {
      const response = await deleteUserGroup(id);
      if (response.ok) {
        store.dispatch(deleteUserGroupRedux(id));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error deleting group: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  const openEditModal = (group: UserGroup) => {
    setEditedGroup(group);
    setEditModalVisible(true);
  };

  const editGroup = async (
    group: UserGroup,
    groupName: string,
    roles: SelectOptions[],
  ) => {
    const roleList = roles.map((role) => role.value);
    try {
      const response = await editUserGroup(group.id, groupName, roleList);
      if (response.ok) {
        setEditModalVisible(false);
        const updatedGroup = {
          ...group,
          groupName: groupName,
          roles: userRole.filter((item) => roleList.includes(item.id)),
        };
        store.dispatch(editUserGroupRedux(updatedGroup));
      } else {
        const errorData = await response.json();
        toast.error(errorData.error, { position: 'bottom-center' });
      }
    } catch (error) {
      toast.error('Error editing group: ' + error, {
        position: 'bottom-center',
      });
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mt: 2.5, mb: 2 }}>
        <AdminSearchBar
          searchTerm={searchTerm}
          handleChange={handleSearchTermChange}
        />
        <MuiButton
          variant='text'
          startIcon={<RefreshIcon />}
          onClick={() => store.dispatch(fetchUserGroups())}
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            textTransform: 'capitalize',
            fontSize: '0.875rem',
            gap: 0.5,
          }}
        >
          Refresh
        </MuiButton>
        <Box sx={{ flex: 1 }} />
        <PermissionGate requiredPermissions={[Permission.GROUPS_CREATE]}>
          <MuiButton
            variant='contained'
            startIcon={<AddIcon />}
            onClick={() => setAddModalVisible(true)}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2,
              bgcolor: '#093725',
              '&:hover': { bgcolor: '#062619' },
            }}
          >
            Add Group
          </MuiButton>
        </PermissionGate>
      </Box>

      <Paper
        variant='outlined'
        sx={{
          borderRadius: '6px',
          borderColor: 'divider',
          overflow: 'hidden',
          boxShadow: 'none',
        }}
      >
        <TableContainer>
          <Table sx={{ minWidth: 500 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'rgba(248, 250, 252, 0.95)' }}>
                <TableCell padding='checkbox' sx={{ pl: 2, width: 48 }}>
                  <Checkbox size='small' />
                </TableCell>
                <TableCell
                  sx={{
                    py: 1.5,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    color: 'text.secondary',
                  }}
                >
                  Group Name
                </TableCell>
                <TableCell align='right' sx={{ py: 1.5, pr: 2, width: 100 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={3} sx={{ py: 6, px: 2 }}>
                    <TableSkeletonLoader numRows={4} height={10} />
                  </TableCell>
                </TableRow>
              ) : paginatedGroups.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={3}
                    sx={{ py: 8, textAlign: 'center', color: 'text.secondary', fontSize: '0.875rem' }}
                  >
                    No user groups found.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedGroups.map((group: UserGroup) => (
                  <GroupRow
                    key={group.id}
                    group={group}
                    deleteGroup={deleteGroup}
                    onEditClick={openEditModal}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <PaginationController
        pageNumber={pageNumber}
        maxPageNumber={totalPages}
        navigateNextPage={() => setPageNumber((pageNumber) => pageNumber + 1)}
        navigatePreviousPage={() =>
          setPageNumber((pageNumber) => pageNumber - 1)
        }
      />
      <Modal
        visible={addModalVisible}
        modalContent={
          <AddGroupModal userRoleList={userRole} addGroup={addGroup} />
        }
        toggle={() => setAddModalVisible(false)}
        headerText='Add Group'
        headerClass={baseStyles.modalHeader}
      />
      <Modal
        visible={editModalVisible}
        modalContent={
          <EditGroupModal
            group={editedGroup || ({} as UserGroup)}
            userRoleList={userRole}
            editGroup={editGroup}
          />
        }
        toggle={() => setEditModalVisible(false)}
        headerText='Edit Roles in Group'
        headerClass={baseStyles.modalHeader}
      />
    </>
  );
};
